/**
 * Utilitários para parse e validação de arquivos CSV
 * Usado no sistema de importação de listas para campanhas
 */

// =====================================================
// TIPOS
// =====================================================

/** Representa uma linha do CSV parseada */
export interface CSVRow {
  [key: string]: string;
}

/** Resultado do parse de um arquivo CSV */
export interface CSVParseResult {
  headers: string[];
  rows: CSVRow[];
  totalRows: number;
  errors: string[];
}

/** Status de validação de uma linha */
export interface RowValidation {
  rowIndex: number;
  row: CSVRow;
  isValid: boolean;
  errors: string[];
  phoneNormalized?: string;
  isDuplicate?: boolean;
  /** Índice da linha original quando esta é uma duplicata (para vincular ao mesmo contato) */
  duplicateOfIndex?: number;
  existsInSystem?: boolean;
  contactId?: string;
  /** Indica se o contato está na blacklist LGPD */
  isBlacklisted?: boolean;
  /** Motivo do bloqueio na blacklist */
  blacklistReason?: string;
}

// =====================================================
// CONSTANTES
// =====================================================

/** Colunas obrigatórias no CSV */
export const REQUIRED_COLUMNS = ['telefone'];

/** Colunas reservadas do sistema (não são variáveis) */
export const SYSTEM_COLUMNS = ['nome', 'telefone', 'email'];

/** Colunas do modelo WhatsApp Oficial (para ORDER_DETAILS, URL dinâmicas, etc.) */
export const OFFICIAL_COLUMNS = [
  'pix_code',         // Código PIX copia-cola completo
  'pix_merchant',     // Nome do recebedor PIX
  'pix_key',          // Chave PIX
  'pix_key_type',     // Tipo: CNPJ, CPF, EMAIL, CELULAR, ALEATORIA
  'pix_amount',       // Valor em reais (ex: 149.90)
  'pix_description',  // Descrição do item/serviço
  'url_suffix',       // Sufixo para URLs dinâmicas de botões
];

/** Conteúdo do arquivo modelo CSV padrão */
export const CSV_TEMPLATE = `nome,telefone,variavel1,variavel2,variavel3
João Silva,5562999998888,Bronze,São Paulo,Produto A
Maria Santos,5511988887777,Prata,Rio de Janeiro,Produto B
Pedro Costa,5521977776666,Ouro,Belo Horizonte,Produto C`;

/** Conteúdo do arquivo modelo CSV para WhatsApp Oficial (com campos PIX e URL dinâmica) */
export const CSV_TEMPLATE_OFICIAL = `nome,telefone,email,pix_code,pix_merchant,pix_key,pix_key_type,pix_amount,pix_description,url_suffix,variavel1,variavel2
João Silva,5562999998888,joao@email.com,00020126580014br.gov.bcb.pix0136abc12345-6789-0abc-def0-123456789abc5204000053039865802BR5925EMPRESA LTDA6008BRASILIA62070503***6304ABCD,Empresa LTDA,12345678000190,CNPJ,149.90,Mensalidade Premium,ABC123,Bronze,São Paulo
Maria Santos,5511988887777,maria@email.com,00020126580014br.gov.bcb.pix0136def12345-6789-0abc-def0-987654321fed5204000053039865802BR5925EMPRESA LTDA6008BRASILIA62070503***6304EFGH,Empresa LTDA,12345678000190,CNPJ,199.90,Plano Pro,DEF456,Prata,Rio de Janeiro`;

// =====================================================
// FUNÇÕES DE PARSE
// =====================================================

/**
 * Detecta automaticamente o delimitador usado no CSV
 * Suporta vírgula (,) e ponto-e-vírgula (;)
 * @param firstLine - Primeira linha do CSV (header)
 * @returns Delimitador detectado
 */
export function detectDelimiter(firstLine: string): string {
  const commaCount = (firstLine.match(/,/g) || []).length;
  const semicolonCount = (firstLine.match(/;/g) || []).length;
  
  // Usa ponto-e-vírgula se tiver mais ocorrências que vírgula
  return semicolonCount > commaCount ? ';' : ',';
}

/**
 * Opções do parseCSV.
 *
 * RELEASE: 2026-05-18 v1.1.0 - Adicionado `requiredColumns` para que cada
 * consumidor (contatos/campanhas, negócios, clientes) defina suas próprias
 * colunas obrigatórias. Default mantém ['telefone'] por compatibilidade
 * com o fluxo histórico de listas de campanha.
 */
export interface ParseCSVOptions {
  /**
   * Lista de colunas obrigatórias (lowercase). Passe `[]` para desabilitar
   * a checagem no parser quando a validação for feita em outra camada
   * (ex.: Step de mapeamento + RPC server-side).
   */
  requiredColumns?: readonly string[];
}

/**
 * Faz o parse de um arquivo CSV string
 * Suporta auto-detecção de delimitador (vírgula ou ponto-e-vírgula)
 * @param csvContent - Conteúdo do arquivo CSV
 * @param options - Opções (ex.: requiredColumns customizado por domínio)
 * @returns Resultado do parse com headers e rows
 */
export function parseCSV(
  csvContent: string,
  options?: ParseCSVOptions,
): CSVParseResult {
  const errors: string[] = [];
  const lines = csvContent.trim().split(/\r?\n/);
  
  if (lines.length < 2) {
    return {
      headers: [],
      rows: [],
      totalRows: 0,
      errors: ['Arquivo CSV vazio ou sem dados']
    };
  }
  
  // Auto-detectar delimitador baseado na primeira linha
  const delimiter = detectDelimiter(lines[0]);
  
  // Parse do header com delimitador detectado
  const headerLine = lines[0];
  const headers = parseCSVLine(headerLine, delimiter).map(h => h.toLowerCase().trim());
  
  // Validar colunas obrigatórias (override por domínio, default = contatos)
  const requiredCols = options?.requiredColumns ?? REQUIRED_COLUMNS;
  for (const required of requiredCols) {
    if (!headers.includes(required)) {
      errors.push(`Coluna obrigatória "${required}" não encontrada`);
    }
  }
  
  if (errors.length > 0) {
    return { headers, rows: [], totalRows: 0, errors };
  }
  
  // Parse das linhas de dados com mesmo delimitador
  const rows: CSVRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Pular linhas vazias
    
    const values = parseCSVLine(line, delimiter);
    const row: CSVRow = {};
    
    headers.forEach((header, index) => {
      row[header] = values[index]?.trim() || '';
    });
    
    rows.push(row);
  }
  
  return {
    headers,
    rows,
    totalRows: rows.length,
    errors
  };
}

/**
 * Faz o parse de uma linha do CSV respeitando aspas
 * @param line - Linha do CSV
 * @param delimiter - Delimitador a usar (padrão: vírgula)
 * @returns Array de valores
 */
export function parseCSVLine(line: string, delimiter: string = ','): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Aspas escapadas
        current += '"';
        i++;
      } else {
        // Toggle estado de aspas
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      // Fim do campo
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  // Último campo
  result.push(current);
  
  return result;
}

// =====================================================
// FUNÇÕES DE NORMALIZAÇÃO
// =====================================================

/**
 * Normaliza telefone adicionando código de país (DDI) quando ausente
 * 
 * @param phone - Telefone original do CSV
 * @param defaultCountryCode - Código do país padrão (ex: '55' para Brasil)
 * @returns Objeto com telefone normalizado e flag indicando se foi modificado
 * 
 * Exemplos com DDI 55 (Brasil):
 * - "62999998888" (11 dígitos) → "5562999998888" (celular)
 * - "6232221111" (10 dígitos) → "556232221111" (fixo)
 * - "5562999998888" (já com DDI) → "5562999998888" (mantém)
 * - "11988887777" (11 dígitos) → "5511988887777"
 */
export function normalizePhoneWithCountryCode(
  phone: string, 
  defaultCountryCode: string = '55'
): { normalized: string; wasModified: boolean } {
  // Remove tudo que não é dígito
  const digits = phone.replace(/\D/g, '');
  
  if (!digits) return { normalized: '', wasModified: false };
  
  // Se já começa com o código de país, retorna como está
  if (digits.startsWith(defaultCountryCode)) {
    return { normalized: digits, wasModified: false };
  }
  
  // Verifica se é número sem DDI baseado no tamanho
  // Para Brasil (55): 10-11 dígitos = DDD + número
  // Para outros países, também usa 10-11 como referência
  if (digits.length >= 10 && digits.length <= 11) {
    return { 
      normalized: defaultCountryCode + digits, 
      wasModified: true 
    };
  }
  
  // Retorna sem modificar para outros formatos
  return { normalized: digits, wasModified: false };
}

// =====================================================
// FUNÇÕES DE VALIDAÇÃO
// =====================================================

/**
 * Valida formato de telefone brasileiro
 * @param phone - Telefone a validar
 * @returns Objeto com resultado da validação
 */
export function validatePhoneFormat(phone: string): { valid: boolean; error?: string } {
  if (!phone || phone.trim() === '') {
    return { valid: false, error: 'Telefone é obrigatório' };
  }
  
  // Remove caracteres não numéricos
  const digits = phone.replace(/\D/g, '');
  
  // Deve começar com 55 (código do Brasil)
  if (!digits.startsWith('55')) {
    return { valid: false, error: 'Deve começar com 55 (código do Brasil)' };
  }
  
  // Deve ter entre 12 e 13 dígitos (55 + DDD + número)
  if (digits.length < 12 || digits.length > 13) {
    return { valid: false, error: 'Formato inválido. Use: 55 + DDD + número (12-13 dígitos)' };
  }
  
  // Validar DDD (2 dígitos após 55, de 11 a 99)
  const ddd = parseInt(digits.substring(2, 4), 10);
  if (ddd < 11 || ddd > 99) {
    return { valid: false, error: 'DDD inválido' };
  }
  
  return { valid: true };
}

/**
 * Extrai colunas de variáveis (excluindo colunas do sistema)
 * @param headers - Array de headers do CSV
 * @returns Array de nomes de variáveis
 */
export function extractVariableColumns(headers: string[]): string[] {
  return headers.filter(h => !SYSTEM_COLUMNS.includes(h.toLowerCase()));
}

/**
 * Gera hash único para identificar a lista
 * @returns String hash única
 */
export function generateListHash(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `${timestamp}-${random}`.toUpperCase();
}

/**
 * Formata bytes para exibição legível
 * @param bytes - Tamanho em bytes
 * @returns String formatada (ex: "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Cria arquivo modelo CSV para download
 */
export function downloadCSVTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', 'modelo_lista_campanha.csv');
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}

/**
 * Cria arquivo modelo CSV para WhatsApp Oficial (com campos PIX e URL dinâmica)
 */
export function downloadCSVTemplateOficial() {
  const blob = new Blob([CSV_TEMPLATE_OFICIAL], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', 'modelo_lista_whatsapp_oficial.csv');
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
}
